// 2024 Ammunition (SRD 5.2.1): a weapon with the Ammunition property needs its ammunition to attack, and each attack
// expends one piece. After a fight, spending 1 minute recovers half the ammunition used (round down).
//
// Owned shots come from the character's ammunition items: "Arrows (20)" counts 20, and a quantity multiplies it.
// Shots spent live in character.ammunition[type] = { spent, recoverable }, so inventory saves never erase them:
// remaining = owned - spent. Buying more ammunition adds to what is owned.
import { weaponHasProperty } from './weaponRange.mjs';

export const AMMUNITION_TYPES = Object.freeze({
  arrow: Object.freeze({ label: 'Arrows', pack: 20, match: /\barrows?\b/ }),
  bolt: Object.freeze({ label: 'Crossbow Bolts', pack: 20, match: /\bbolts?\b/ }),
  needle: Object.freeze({ label: 'Blowgun Needles', pack: 50, match: /\bneedles?\b/ }),
  'firearm-bullet': Object.freeze({ label: 'Firearm Bullets', pack: 10, match: /\bfirearm bullets?\b/ }),
  'sling-bullet': Object.freeze({ label: 'Sling Bullets', pack: 20, match: /\bsling bullets?\b/ }),
});

const WEAPON_AMMUNITION = Object.freeze({
  shortbow: 'arrow', longbow: 'arrow',
  'light-crossbow': 'bolt', 'hand-crossbow': 'bolt', 'heavy-crossbow': 'bolt',
  blowgun: 'needle', musket: 'firearm-bullet', pistol: 'firearm-bullet', sling: 'sling-bullet',
});

const slug = (value) => String(value || '').trim().toLowerCase().replace(/['’]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

/** The ammunition a weapon fires, or null (no Ammunition property, or an unknown base weapon). */
export const weaponAmmunitionType = (weapon) => {
  if (!weaponHasProperty(weapon, 'ammunition')) return null;
  return [weapon?.index, weapon?.type, weapon?.weaponType, weapon?.name].map(slug).map((key) => WEAPON_AMMUNITION[key]).find(Boolean) || null;
};

const itemName = (entry) => String((entry?.item || entry?.data || entry)?.name || '').trim().toLowerCase();

/** Pieces of one ammunition type carried in the character's items: pack size from "(20)" in the name, times quantity. */
export const ownedAmmunition = (character, type) => {
  const definition = AMMUNITION_TYPES[type];
  if (!definition) return 0;
  const entries = [...(Array.isArray(character?.item) ? character.item : []), ...(Array.isArray(character?.inventory) ? character.inventory : [])];
  return entries.reduce((total, entry) => {
    const name = itemName(entry);
    // Sling and firearm bullets share "bullets"; each type's pattern names its own.
    if (!definition.match.test(name)) return total;
    if (type !== 'firearm-bullet' && type !== 'sling-bullet' && /\bbullets?\b/.test(name)) return total;
    const pack = Number(/\((\d+)\)/.exec(name)?.[1]) || definition.pack;
    const quantity = Number(entry?.quantity ?? entry?.count ?? entry?.item?.quantity ?? 1);
    return total + pack * (Number.isFinite(quantity) && quantity > 0 ? quantity : 1);
  }, 0);
};

const ammunitionState = (character, type) => character?.ammunition?.[type] || {};

/** Shots left of one ammunition type. */
export const remainingAmmunition = (character, type) => Math.max(0,
  ownedAmmunition(character, type) - Math.max(0, Number(ammunitionState(character, type).spent) || 0));

/** Shots a recovery would return: half the ammunition used since the last recovery, rounded down. */
export const recoverableAmmunition = (character, type) => Math.floor(Math.max(0, Number(ammunitionState(character, type).recoverable) || 0) / 2);

/** The next ammunition state after one shot, or null when none is left. */
export const spendAmmunition = (character, type) => {
  if (remainingAmmunition(character, type) < 1) return null;
  const current = ammunitionState(character, type);
  return { spent: Math.max(0, Number(current.spent) || 0) + 1, recoverable: Math.max(0, Number(current.recoverable) || 0) + 1 };
};

/** The ammunition field after recovering half of every type used since the last recovery. */
export const recoverAmmunition = (character) => Object.fromEntries(Object.entries(character?.ammunition || {})
  .filter(([type]) => AMMUNITION_TYPES[type])
  .map(([type, state]) => [type, { spent: Math.max(0, (Number(state?.spent) || 0) - recoverableAmmunition(character, type)), recoverable: 0 }]));
